// The only place in the app that calls fetch. Every reader returns null for a missing or
// unreadable file and records why, so an optional file degrades to empty data.
//
// Dev servers answer unknown paths with index.html and a 200, so a text/html response counts as
// missing too.

import type { Fetcher } from './types.ts';

const HTML_TYPE = 'text/html';

export interface Reader {
  json<T>(path: string): Promise<T | null>;
  buffer(path: string): Promise<ArrayBuffer | null>;
  /** Missing/unreadable files seen so far. */
  warnings: string[];
}

function defaultFetch(): Fetcher {
  return (url) => fetch(url);
}

/** A reader rooted at `baseUrl` ('/data' or 'http://host/data'). */
export function makeReader(baseUrl: string, fetcher: Fetcher = defaultFetch()): Reader {
  const base = baseUrl.replace(/\/+$/, '');
  const warnings: string[] = [];

  function warn(path: string, why: string): null {
    const msg = `data: ${path} ${why}`;
    warnings.push(msg);
    console.warn(msg);
    return null;
  }

  async function get(path: string): Promise<Response | null> {
    let res: Response;
    try {
      res = await fetcher(`${base}/${path}`);
    } catch (err) {
      return warn(path, `could not be fetched (${String(err)})`);
    }
    if (!res.ok) {
      return warn(path, `is missing (HTTP ${res.status})`);
    }
    if ((res.headers.get('content-type') ?? '').includes(HTML_TYPE)) {
      return warn(path, 'is missing (server answered with HTML)');
    }
    return res;
  }

  async function json<T>(path: string): Promise<T | null> {
    const res = await get(path);
    if (!res) {
      return null;
    }
    try {
      return (await res.json()) as T;
    } catch (err) {
      return warn(path, `is not valid JSON (${String(err)})`);
    }
  }

  async function buffer(path: string): Promise<ArrayBuffer | null> {
    const res = await get(path);
    return res ? res.arrayBuffer() : null;
  }

  return { json, buffer, warnings };
}
